import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useMe } from '../api/queries/auth.ts'
import { defineMessages, useT } from '../i18n/index.ts'
import { useStartVisit } from '../lib/visit.ts'
import { Loading } from '../ui/Surface.tsx'

const messages = defineMessages({
	en: { gettingReady: 'Getting ready', checking: 'Checking your session' },
	ro: { gettingReady: 'Se pregătește', checking: 'Se verifică sesiunea' },
	ru: { gettingReady: 'Подготовка', checking: 'Проверяем сеанс' },
	fr: { gettingReady: 'Préparation', checking: 'Vérification de votre session' },
	it: { gettingReady: 'Un attimo', checking: 'Controllo della tua sessione' },
	kk: { gettingReady: 'Дайындалуда', checking: 'Сеансты тексеріп жатырмыз' },
	es: { gettingReady: 'Preparando', checking: 'Comprobando tu sesión' },
	pt: { gettingReady: 'Preparando', checking: 'Verificando sua sessão' },
	de: { gettingReady: 'Einen Moment', checking: 'Deine Sitzung wird geprüft' },
})

/**
 * The guard asks the server, because the session is an httpOnly cookie and the client cannot
 * see it. Until `useMe` resolves there is no answer to give, so the page waits rather than
 * flashing the login screen at someone who is already signed in.
 */
export function RequireTeacher({ children }: { children: ReactNode }) {
	const t = useT(messages)
	const { data: teacher, isPending } = useMe()
	const location = useLocation()
	useStartVisit(!!teacher)

	if (isPending) return <Loading label={t.gettingReady} />
	if (!teacher) return <Navigate to="/login" replace state={{ from: location.pathname }} />
	return <>{children}</>
}

/**
 * The mirror image: sign-in and sign-up are pointless with an account. A guest is exactly who
 * they are for, so she is let through.
 */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
	const t = useT(messages)
	// No guest here: she came to sign in, not to start work.
	const { data: teacher, isPending } = useMe({ guest: false })
	useStartVisit(!!teacher)
	if (isPending) return <Loading label={t.checking} />
	if (teacher && !teacher.guest) return <Navigate to="/app" replace />
	return <>{children}</>
}
